import moment from "moment";
import momentTz from "moment-timezone";

/**
 * Tomorrow in New York, as a local-midnight `Date` so `moment(date).format`
 * reads back the same day. The browser's tomorrow is New York's today for a
 * customer out west in the evening, and the server refuses an ordinary
 * booking for today (it has to be Same-Day).
 */
function businessTomorrow(): Date {
  const [year, month, day] = momentTz
    .tz("America/New_York")
    .add(1, "day")
    .format("YYYY-MM-DD")
    .split("-")
    .map(Number);
  return new Date(year, month - 1, day);
}

export function getNextAvailableDate(cleanDate: string, bookedDates?: string[]) {
  const date = cleanDate ? new Date(cleanDate) : businessTomorrow();
  if (!bookedDates?.length) {
    return date;
  }
  let formattedDate = moment(date).format("YYYY-MM-DD");
  while (bookedDates.includes(formattedDate)) {
    date.setDate(date.getDate() + 1);
    formattedDate = moment(date).format("YYYY-MM-DD");
  }
  return date;
}

/**
 * How far past the next open day to look for one at the standard price. Two
 * weeks covers every weekday twice over, so a run of booked days in front of
 * the only unsurged weekday cannot hide it.
 */
const STANDARD_PRICE_HORIZON_DAYS = 14;

/**
 * The date the booking step opens on: the next open day that is not a Busy Day.
 *
 * The first price a customer sees should be the baseline one. Opening on the
 * next open day put a surged Saturday in front of anyone booking on a Friday,
 * and the first number they read was the dearest one of the week (26 Sep
 * feedback). A Busy Day is still one click away; it is just not the default.
 *
 * Falls back to the next open day when nothing in the horizon is unsurged —
 * every day surged, or every unsurged day booked — so there is always a date.
 */
export function getDefaultBookingDate(
  bookedDates: string[] | undefined,
  isBusyDay: (date: string) => boolean
): Date {
  const first = getNextAvailableDate("", bookedDates);
  const booked = new Set(bookedDates ?? []);
  const candidate = new Date(first);
  for (let i = 0; i <= STANDARD_PRICE_HORIZON_DAYS; i++) {
    const formatted = moment(candidate).format("YYYY-MM-DD");
    if (!booked.has(formatted) && !isBusyDay(formatted)) return candidate;
    candidate.setDate(candidate.getDate() + 1);
  }
  return first;
}
